import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtErr, fmtQty, todayISO } from "../../api";
import MfgTabs from "../../components/MfgTabs";

export default function MOCreate() {
  const [products, setProducts] = useState([]);
  const [boms, setBoms] = useState([]);
  const [productId, setProductId] = useState("");
  const [qty, setQty] = useState("");
  const [date, setDate] = useState(todayISO());
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/products", { params: { product_type: "Finished Good", active: "true" } }).then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!productId) { setBoms([]); return; }
    api.get("/boms", { params: { product_id: productId } }).then((r) => setBoms(r.data.filter((b) => b.active))).catch(() => {});
  }, [productId]);

  const product = products.find((p) => p.id === productId);
  const bom = boms[0];

  useEffect(() => {
    const b = boms[0];
    if (b?.standard_batch_size) setQty((q) => q || String(b.standard_batch_size));
  }, [boms]);
  const q = parseFloat(qty) || 0;
  const requirements = bom && q > 0
    ? bom.components.map((c) => ({ ...c, required: (c.qty * q) / bom.basis_qty }))
    : [];
  const estHours = product?.production_rate > 0 && q > 0 ? (q / product.production_rate).toFixed(2) : null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/mos", { product_id: productId, planned_qty: q, planned_date: date, notes });
      toast.success(`${r.data.mo_number} created`);
      navigate(`/manufacturing/orders/${r.data.id}`);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="mo-create-page" className="space-y-6 max-w-3xl">
      <h1 className="font-heading text-3xl font-extrabold text-[#002B5D] tracking-tight">New Manufacturing Order</h1>
      <MfgTabs />
      <form onSubmit={submit} noValidate className="sk-card p-6 space-y-6 mt-4">
        <div>
          <label className="sk-label">Finished Product *</label>
          <select data-testid="mo-product-select" required className="sk-input" value={productId} onChange={(e) => setProductId(e.target.value)}>
            <option value="">Select product...</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.code})</option>)}
          </select>
        </div>

        {productId && (
          <div data-testid="mo-bom-status">
            <label className="sk-label">Active BOM</label>
            {bom ? (
              <div className="text-sm bg-slate-50 border border-border rounded-md px-4 py-3">
                <span className="font-semibold text-[#002B5D]">{bom.name}</span>
                <span className="text-slate-500 ml-2">v{bom.version} — per {fmtQty(bom.basis_qty)} {bom.basis_uom}</span>
                <div className="text-xs text-slate-500 mt-1">
                  Standard batch: <span className="font-mono font-semibold">{fmtQty(bom.standard_batch_size)} {bom.basis_uom}</span>
                  {bom.production_rate ? <> · Rate: <span className="font-mono font-semibold">{fmtQty(bom.production_rate)} {bom.basis_uom}/h</span></> : null}
                </div>
              </div>
            ) : (
              <div className="text-sm bg-rose-50 border border-rose-200 text-rose-700 rounded-md px-4 py-3" data-testid="mo-no-bom-warning">
                No active BOM for this product. Create one in the BOMs tab first.
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="sk-label">Planned Quantity * {product && <span className="normal-case text-slate-400">({product.uom})</span>}</label>
            <input data-testid="mo-qty-input" type="number" step={product?.qty_step || 1} min="0" required inputMode="numeric" className="sk-input font-mono" value={qty} onChange={(e) => setQty(e.target.value)} />
          </div>
          <div>
            <label className="sk-label">Planned Date</label>
            <input data-testid="mo-date-input" type="date" className="sk-input" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
        </div>

        {requirements.length > 0 && (
          <div data-testid="mo-requirements-preview">
            <label className="sk-label">Required Raw Materials (auto-calculated)</label>
            <table className="w-full sk-table border border-border rounded-md">
              <thead><tr><th>Material</th><th>Code</th><th className="text-right">Required</th><th>UOM</th></tr></thead>
              <tbody>
                {requirements.map((r) => (
                  <tr key={r.product_id} data-testid={`mo-req-${r.product_code}`}>
                    <td>{r.product_name}</td>
                    <td className="font-mono text-xs text-slate-500">{r.product_code}</td>
                    <td className="text-right font-mono font-semibold text-[#002B5D]">{fmtQty(r.required)}</td>
                    <td>{r.uom}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {estHours && (
          <div className="text-sm bg-sky-50 border border-sky-200 text-sky-800 rounded-md px-4 py-3" data-testid="mo-est-time">
            Estimated manufacturing time: <span className="font-mono font-bold">{estHours} hours</span>
            <span className="text-sky-600 ml-1">at {fmtQty(product.production_rate)} {product.uom}/hour</span>
          </div>
        )}

        <div>
          <label className="sk-label">Notes</label>
          <input data-testid="mo-notes-input" className="sk-input" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>

        <button data-testid="mo-create-submit" disabled={busy || !bom || q <= 0}
          className="w-full h-12 rounded-md font-heading font-bold text-white disabled:opacity-50" style={{ backgroundColor: "#EB1C27" }}>
          {busy ? "Creating..." : "Create Manufacturing Order"}
        </button>
      </form>
    </div>
  );
}
